import React, { useState, useRef, useEffect } from "react";
import { motion, useAnimation } from "framer-motion";
import tadStart from "../../@shared/assets/toshi-start.png";
import tadFin from "../../@shared/assets/toshi-fin.png";
import criticalBg from "../../@shared/assets/criticalbg.gif";
import AudioManager from "../../@shared/utils/AudioManager";
import { Typography } from "@mui/material";

const FightAnimation = ({
  attackAnimation,
  character,
  attackedCharacter,
  criticalHit,
  massive,
  damageDealt,
  onFinish,
  isPositive = null,
}) => {
  const [strikePose, setStrikePose] = useState(false);
  const animationCooldownRef = useRef(false);

  const characterControls = useAnimation();
  const character2Controls = useAnimation();
  const flashControls = useAnimation();

  const imageStart = character?.imageStart;
  const imageFin = character?.imageFin;

  // For reflected hits: try to use a “hit” pose on the ATTACKER if available.
  const attackerHitImage =
    character?.imageHit ||
    character?.imageHurt ||
    character?.imageDamaged ||
    character?.imageDamage ||
    null;

  const imageStart2 = attackedCharacter?.imageStart;
  const imageFin2 = attackedCharacter?.imageHit;

  const hasHitPose = attackedCharacter?.imageHit;

  const isReflected =
    attackAnimation?.type === "attack - reflected" ||
    attackAnimation?.reflected === true;

  const getCardImage = (card) =>
    card?.imageUrl ||
    card?.image ||
    card?.img ||
    card?.cardImage ||
    card?.cardArt ||
    null;

  const mirrorCardImage = getCardImage(attackAnimation?.card);

  const targetX = 0; // same as native logic
  const target2X = 60; // same as native logic

  const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

  const finishAnimation = () => {
    setStrikePose(false);
    if (onFinish) onFinish();
  };

  const startAttackAnimation = async () => {
    await characterControls.start({ x: targetX }, { duration: 0.25, ease: "easeInOut" });
    await character2Controls.start({ x: target2X }, { duration: 0.25, ease: "easeInOut" });

    setStrikePose(true);
    AudioManager.playSFX("playerHit");

    await Promise.all([
      flashControls.start({ opacity: [0, 1, 0, 1, 0] }, { duration: 0.2, ease: "linear" }),
      characterControls.start(
        { x: [targetX, targetX - 10, targetX + 10, targetX - 5, targetX] },
        { duration: 0.2, ease: "easeInOut" }
      ),
      character2Controls.start(
        { x: [target2X, target2X + 10, target2X - 10, target2X + 5, target2X], scaleX: -1 },
        { duration: 0.2, ease: "easeInOut" }
      ),
    ]);

    await delay(1500);

    await characterControls.start({ x: -400 }, { duration: 0.3, ease: "easeInOut" });
    await character2Controls.start({ x: 500 }, { duration: 0.3, ease: "easeInOut" });

    finishAnimation();
  };

  // ✅ NEW: Mirror Shield reflected flow
  const startReflectedAnimation = async () => {
    // attacker comes in from left
    await characterControls.start({ x: targetX }, { duration: 0.25, ease: "easeInOut" });

    // mirror shield card comes in from right
    await character2Controls.start({ x: target2X }, { duration: 0.25, ease: "easeInOut" });

    // switch attacker to “hit” pose (or fallback) + shake
    setStrikePose(true);
    AudioManager.playSFX("playerHit");

    await Promise.all([
      flashControls.start({ opacity: [0, 1, 0, 1, 0] }, { duration: 0.2, ease: "linear" }),

      // attacker shake (stronger to sell reflect)
      characterControls.start(
        { x: [targetX, targetX - 14, targetX + 14, targetX - 8, targetX] },
        { duration: 0.22, ease: "easeInOut" }
      ),

      // mirror card shakes once on impact
      character2Controls.start(
        { x: [target2X, target2X + 8, target2X - 8, target2X] },
        { duration: 0.18, ease: "easeInOut" }
      ),
    ]);

    await delay(1500);

    // retreat
    await characterControls.start({ x: -400 }, { duration: 0.3, ease: "easeInOut" });
    await character2Controls.start({ x: 600 }, { duration: 0.3, ease: "easeInOut" });

    finishAnimation();
  };

  useEffect(() => {
    if (!attackAnimation) return;
    if (animationCooldownRef.current) return;

    animationCooldownRef.current = true;

    setTimeout(() => {
      if (isReflected) startReflectedAnimation();
      else startAttackAnimation();
    }, 75);

    setTimeout(() => {
      animationCooldownRef.current = false;
    }, 2000);
  }, [attackAnimation, isReflected]);

  return (
    <div
      style={{
        height: "100%",
        position: "absolute",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        pointerEvents: "none",
      }}
    >
      {/* Background Layer */}
      {criticalHit || massive ? (
        <div
          style={{
            height: "100%",
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundImage: `url(${criticalBg})`,
            backgroundSize: "cover",
            opacity: 0.7,
            zIndex: 0,
          }}
        />
      ) : null}

      {/* Foreground Layer */}
      <div
        style={{
          position: "relative",
          zIndex: 1,
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          height: "100%",
          width: "100%",
          containerType: "inline-size",
        }}
      >
        {(criticalHit && !massive) && (
          <motion.div
            initial={{ transform: "skewX(-15deg)", opacity: 0.5, top: "20%", left: "20%" }}
            animate={{ opacity: 1, top: "20%", left: "30%" }}
            style={{ position: "absolute", top: "20%", left: "30%" }}
          >
            <Typography
              style={{
                fontWeight: 900,
                fontSize: "8cqw",
                color: isPositive ? "green" : "red",
                textShadow:
                  " -3px -3px 0 white, 3px -3px 0 white, -3px  3px 0 white, 3px  3px 0 white",
                zIndex: 2,
              }}
            >
              CRITICAL!
            </Typography>
          </motion.div>
        )}

        {massive && (
          <motion.div
            initial={{ transform: "skewX(-15deg)", opacity: 0.5, top: "20%", left: "20%" }}
            animate={{ opacity: 1, top: "20%", left: "30%" }}
            style={{ position: "absolute", top: "20%", left: "30%", zIndex: 2 }}
          >
            <Typography
              style={{
                fontWeight: 900,
                fontSize: "8cqw",
                color: isPositive ? "green" : "red",
                textShadow:
                  " -3px -3px 0 white, 3px -3px 0 white, -3px  3px 0 white, 3px  3px 0 white",
              }}
            >
              MASSIVE!
            </Typography>
          </motion.div>
        )}

        <motion.div
          initial={{ transform: "skewX(-15deg)", opacity: 0.5, bottom: "30%", right: "10%" }}
          animate={{ opacity: 1, bottom: "30%", right: "20%" }}
          style={{ position: "absolute", bottom: "30%", right: "20%", zIndex: 5 }}
        >
          <Typography
            style={{
              fontWeight: 900,
              fontSize: "8cqw",
              color: isPositive ? "green" : "red",
              textShadow:
                " -3px -3px 0 white, 3px -3px 0 white, -3px  3px 0 white, 3px  3px 0 white",
            }}
          >
            {`${isPositive ? "+" : "-"}${damageDealt} hp`}
          </Typography>
        </motion.div>

        {/* Flash Overlay */}
        <motion.div
          animate={flashControls}
          initial={{ opacity: 0 }}
          style={{
            position: "absolute",
            top: 0,
            left: 0,
            right: 0,
            bottom: 0,
            backgroundColor: "#fff",
            zIndex: 10,
          }}
        />

        {/* Left entity (attacker) */}
        <motion.img
          src={
            strikePose
              ? isReflected
                ? attackerHitImage || imageStart
                : imageFin
              : imageStart
          }
          animate={characterControls}
          initial={{ x: -400 }}
          style={{
            width: "250px",
            height: "250px",
            position: "absolute",
            bottom: "35%",
            zIndex: 4,
          }}
          alt=""
        />

        {/* Right entity: defender (normal) OR mirror card (reflected) */}
        {(isReflected ? !!mirrorCardImage : hasHitPose) && (
          <motion.img
            src={isReflected ? mirrorCardImage : strikePose ? imageFin2 : imageStart2}
            animate={character2Controls}
            initial={isReflected ? { x: 600 } : { x: 600, scaleX: -1 }}
            style={
              isReflected
                ? {
                    width: "220px",
                    height: "310px",
                    position: "absolute",
                    bottom: "32%",
                    zIndex: 3,
                    objectFit: "contain",
                  }
                : {
                    transform: "scaleX(-1)",
                    width: "250px",
                    height: "250px",
                    position: "absolute",
                    bottom: "35%",
                    zIndex: 3,
                  }
            }
            alt=""
          />
        )}
      </div>
    </div>
  );
};

export default FightAnimation;
